import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
  Fade,
  TextField,
  InputAdornment,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { motion } from "framer-motion";
import { getPresupuestosHistoricos } from "../../api/Estimates/estimatesApi";
import { getFileContent } from "../../api/Proyectos/projectFilesApi";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const VentanaPresupuestosHistoricos = ({
  open,
  onClose,
  tipoPrenda,
  idSubgrupo,
  idCliente,
}) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  // 🖼️ Cache para evitar múltiples peticiones
  const [thumbnails, setThumbnails] = useState({});

  useEffect(() => {
    if (!rows.length) return;

    const loadImages = async () => {
      const results = {};

      for (const r of rows) {
        try {
          const files = await getFileContent(
            r.NumeroPresupuesto || r.IdPresupuesto
          );
          const imgFile = files.find((f) => f.mimeType?.includes("image"));
          if (imgFile) results[r.id] = imgFile;
        } catch (err) {
          console.warn("Sin imagen para:", r.NumeroPresupuesto);
        }
      }

      setThumbnails(results);
    };

    loadImages();
  }, [rows]);

  useEffect(() => {
    if (!open || !tipoPrenda || !idSubgrupo || !idCliente) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getPresupuestosHistoricos(
          tipoPrenda,
          idSubgrupo,
          idCliente
        );
        setRows(data.map((r, i) => ({ id: i, ...r })));
      } catch (err) {
        console.error("❌ Error cargando históricos:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open, tipoPrenda, idSubgrupo, idCliente]);

  // 🔍 Filtrado en vivo
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const lower = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some(
        (v) => typeof v === "string" && v.toLowerCase().includes(lower)
      )
    );
  }, [search, rows]);

  const columns = [
    {
      field: "Foto",
      headerName: "Img",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const file = thumbnails[params.row.id];
        if (!file || !file.data || !file.mimeType) {
          return <span style={{ color: "#ccc" }}>—</span>;
        }

        const src = `data:${file.mimeType};base64,${file.data}`;

        return (
          <Tooltip
            title={
              <Box
                sx={{
                  position: "relative",
                  p: 2,
                  borderRadius: 4,
                  overflow: "hidden",
                  background:
                    "linear-gradient(145deg, rgba(0,0,0,0.6), rgba(0,0,0,0.85))",
                  backdropFilter: "blur(4px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "opacity 0.25s ease",
                }}
              >
                <img
                  src={src}
                  alt={file.fileName || "thumb"}
                  style={{
                    width: 380,
                    height: 380,
                    objectFit: "cover",
                    borderRadius: "12px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1.5,
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.12)",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "6px",
                    fontSize: "0.8rem",
                    letterSpacing: 0.5,
                  }}
                >
                  {params.row.NumeroPresupuesto}
                </Typography>
              </Box>
            }
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "transparent",
                  backdropFilter: "blur(2px)",
                  boxShadow: "none",
                  p: 0,
                  maxWidth: "none !important", // 🚀 elimina el límite
                  width: "auto !important", // permite usar 380px completos
                },
              },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "transform 0.25s ease",
                "&:hover": {
                  transform: "scale(1.25)",
                },
              }}
            >
              <img
                src={src}
                alt={file.fileName || "thumb"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Tooltip>
        );
      },
    },

    {
      field: "NumeroPresupuesto",
      headerName: "Nº",
      width: 145,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "NombreCliente",
      headerName: "Cliente",
      width: 160,
      renderCell: (params) => (
        <Tooltip
          title={
            <>
              <div>
                <strong>Cliente:</strong> {params.row.NombreCliente || "-"}
              </div>
              <div>
                <strong>Diseño:</strong> {params.row.Design || "-"}
              </div>
            </>
          }
          arrow
        >
          <span>{params.value || "-"}</span>
        </Tooltip>
      ),
    },
    { field: "TipoPrenda", headerName: "Tipo", width: 120 },
    { field: "SUBGRUPO", headerName: "Material", width: 160 },
    { field: "Design", headerName: "Design", width: 160 },
    { field: "ProveedorNombre", headerName: "Proveedor", width: 140 },
    { field: "TemporadaNombre", headerName: "Temporada", width: 100 },
    {
      field: "Units",
      headerName: "Units",
      width: 90,
      align: "right",
      headerAlign: "right",
      valueFormatter: (p) =>
        p.value != null ? Number(p.value).toLocaleString("es-ES") : "",
    },
    {
      field: "CosteConvertido",
      headerName: "Coste €",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (p) =>
        p.value
          ? Number(p.value).toLocaleString("es-ES", {
              minimumFractionDigits: 2,
            })
          : "",
    },
    {
      field: "Venta",
      headerName: "Venta €",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (p) =>
        p.value
          ? Number(p.value).toLocaleString("es-ES", {
              minimumFractionDigits: 2,
            })
          : "",
    },
    {
      field: "Margen",
      headerName: "Margen %",
      width: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <strong
          style={{
            color:
              p.value > 20 ? "#00c853" : p.value < 5 ? "#d50000" : "#1976d2",
          }}
        >
          {p.value ? `${p.value.toFixed(1)}%` : ""}
        </strong>
      ),
    },
  ];

  const handleExportExcel = () => {
    if (!rows || rows.length === 0) {
      alert("No hay datos para exportar");
      return;
    }
    const dataToExport = rows.map((r) => ({
      "Nº Presupuesto": r.NumeroPresupuesto || "",
      Cliente: r.NombreCliente || "",
      Diseño: r.Design || "",
      Tipo: r.TipoPrenda || "",
      Material: r.SUBGRUPO || "",
      Proveedor: r.ProveedorNombre || "",
      Temporada: r.TemporadaNombre || "",
      Units: r.Units ?? "",
      "Coste (€)": r.CosteConvertido ?? "",
      "Venta (€)": r.Venta ?? "",
      "Margen (%)": r.Margen ? `${r.Margen.toFixed(1)}%` : "",
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Históricos");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Presupuestos_Historicos_${tipoPrenda || "general"}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          /*   mt: "0vh", */ // 🟢 mueve el diálogo hacia arriba
          borderRadius: 5,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(235,250,247,0.85) 100%)",
          backdropFilter: "blur(18px)",
          border: "1px solid rgba(0,191,165,0.2)",
          boxShadow: "0 8px 35px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryOutlinedIcon sx={{ color: "#00bfa5", fontSize: 28 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#00bfa5",
              letterSpacing: 0.4,
              textShadow: "0 1px 1px rgba(0,0,0,0.05)",
            }}
          >
            Presupuestos Históricos Similares
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Exportar a Excel">
            <IconButton
              onClick={handleExportExcel}
              sx={{
                color: "#34afc7",
                "&.MuiIconButton-root": {
                  border: "1px solid #34afc7",
                },

                "&.MuiButtonBase-root": {
                  marginLeft: "5px",
                  marginTop: "6px",
                  padding: "9px",
                },

                "&:hover": {
                  backgroundColor: "#00bfa5",
                  color: "#fff",
                },
              }}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar">
            <IconButton
              onClick={onClose}
              sx={{
                color: "#34afc7",
                "&.MuiIconButton-root": {
                  border: "1px solid #34afc7",
                },

                "&.MuiButtonBase-root": {
                  marginLeft: "5px",
                  marginTop: "6px",
                  padding: "9px",
                },

                "&:hover": {
                  backgroundColor: "red",
                  color: "#fff",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* SEARCH */}
      <Box sx={{ px: 3, pb: 1 }}>
        <TextField
          fullWidth
          placeholder="Buscar por cliente, proveedor, tipo..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9e9e9e" }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(6px)",
            },
          }}
        />
      </Box>

      <Divider />

      {/* BODY */}
      <Box sx={{ p: 3, pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
            <CircularProgress
              size={48}
              thickness={4}
              sx={{ color: "#00bfa5" }}
            />
          </Box>
        ) : filteredRows.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box
              sx={{
                height: 520,
                borderRadius: 3,
                overflow: "hidden",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "rgba(0,191,165,0.08)",
                  color: "#00695c",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(0,191,165,0.06)",
                  transition: "background-color 0.2s ease",
                },
              }}
            >
              <DataGridPro
                rows={filteredRows}
                columns={columns}
                disableColumnMenu
                pageSizeOptions={[25, 50, 100]}
                onRowDoubleClick={(params) => {
                  if (params.row.IdPresupuesto) {
                    const url = `/estimates?idPresupuesto=${params.row.IdPresupuesto}`;
                    window.open(url, "_blank");
                  }
                }}
              />
            </Box>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                mt: 6,
                color: "#888",
                fontStyle: "italic",
              }}
            >
              No hay presupuestos coincidentes.
            </Typography>
          </motion.div>
        )}
      </Box>
    </Dialog>
  );
};

export default VentanaPresupuestosHistoricos;
